/* eslint-disable yield-star-spacing */
/* eslint-disable generator-star-spacing */
import * as Sentry from '@sentry/react'
import { getFirstStep } from 'Containers/OnboardingSteps'
import { getCurrentVisitUrlParamsString, getMetaPurchaseEventId, getRecurringAndTrialId, getSanitizedMarketingSearchParams, getSearchParams, getUserLanguage, isUserSubscribed, removeLiveUrlParams, removeSearchParams } from 'Lib'
import { DefaultConfig } from 'Lib/Constants'
import { Flows, getCurrentFlow, getDownloadUrl, getPaymentSuccessUrl, getPaymentUrl, getUpgradeAccountUrl } from 'Lib/EventsUtils'
import { convertToFirebaseFormat, removeUndefined } from 'Lib/SelectionsUtils'
import { isPurchaseAvailable } from 'Lib/StripeUtils'
import {
  ActionType,
  IAnonymousSignInRequestAction,
  ICheckEmailExistsRequestAction,
  ICreateStripeSessionAction,
  ILogoutRequestAction,
  IRegisterWithEmailAndPassword,
  IRegisterWithGoogleRequestAction,
  IResetPasswordRequestAction,
  ISaveOnboardingDataRequestAction,
  ISignInWithEmailRequestAction as ISignInWithEmailAndPasswordAction,
  ISignInWithFutokenAction,
  ISignInWithAppleRequestAction,
  ISignInWithGoogleOneTapAction,
  ISignInWithGoogleRequestAction,
  ISignUpWithEmailRequestAction,
  IUpdateUserChangesetRequestAction,
  IUpdateUserRequestAction,
  IUpdateUserSuccessAction,
  IUpgradeUserWithGoogleRequestAction,
  Selectors
} from 'Reducers'
import { ONBOARDING_ROUTES_BY_STEP_ID, RouteNames } from 'RouteNames'
import { saveSourceMetadataSaga, waitForLogin, waitForStartup } from 'Sagas/HelperSagas'
import { takeLatestSafe } from 'Sagas/SagaUtils'
import { ApiService, IFirebaseService, createStripeCheckoutSession } from 'Services'
import { push, replace } from 'connected-react-router'
import { FirebaseError } from 'firebase/app'
import { GoogleAuthProvider, OAuthProvider } from 'firebase/auth'
import { t } from 'i18next'
import { isEqual } from 'lodash'
import { delay, race } from 'redux-saga/effects'
import { firebaseServicePromise } from 'store'
import { all, call, put, select, take } from 'typed-redux-saga'
import { Selections } from 'types/onboardingTypes'

/**
 * This saga gets called not only when the user is updated
 * but also when there is a successful sign-in event
 */
function* updateUserSuccessSaga({
  user
}: IUpdateUserSuccessAction) {
  yield call(updateAnalyticsProperties, user)
}

function* updateAnalyticsProperties(user: IUser) {
  const firebaseService: IFirebaseService = yield firebaseServicePromise

  yield call(firebaseService.updateAnalyticsProperty, 'created', user.created)
  yield call(firebaseService.updateAnalyticsProperty, 'createdISOWeek', user.created)

  yield call(firebaseService.updateAnalyticsProperty, 'gender', user.personal?.gender)
  yield call(firebaseService.updateAnalyticsProperty, 'weight', user.stats?.weight?.[0]?.value)
  yield call(firebaseService.updateAnalyticsProperty, 'wellhubStatus', user.wellhub?.status ?? 'inactive')
}

function* anonymousSignInSaga({ newsletterAccepted, resolve, reject }: IAnonymousSignInRequestAction) {
  const isUserLoggedIn: boolean = yield select(Selectors.isLoggedIn)

  // We don't need to login again if the user is already logged
  // eg. by navigating back from the payment/registration to plan-is-ready
  if (isUserLoggedIn) {
    yield call(resolve)
    return
  }

  const firebaseService: IFirebaseService = yield firebaseServicePromise

  yield put({ type: ActionType.SET_APP_BLOCKED })

  try {
    try {
      const { tokenId } = yield call(firebaseService.anonymousSignIn)

      localStorage.setItem('tokenId', tokenId)
    } catch (error) {
      yield call(logEvent, 'web_anonymousSignUpFailed', error)
      yield call(reject, error)
      return
    }

    // Wait until user callback from firebase and we have the user in our store
    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(logEvent, 'web_signUpSuccess', { newsletterConfirmed: newsletterAccepted })

    yield call(resolve)
  } finally {
    // Must also run when the worker is cancelled (domain teardown).
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* signUpWithEmailSaga({ email, password, onSuccess, onError }: ISignUpWithEmailRequestAction) {
  const firebaseService: IFirebaseService = yield firebaseServicePromise

  try {
    yield put({ type: ActionType.SET_APP_BLOCKED })
    yield call(firebaseService.createUserWithEmailAndPassword, email, password)

    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(updateOnboardingActiveFlagSaga)
    yield call(logEvent, 'web_signUpSuccess')

    yield call(onSuccess)
  } catch (e) {
    yield call(logEvent, 'web_signUpFail', e)

    yield call(onError, e)

    yield put({ type: ActionType.SIGN_UP_WITH_EMAIL_FAILED, error: e })
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* signInWithEmailAndPasswordSaga({ email, password, onSuccess, onError }: ISignInWithEmailAndPasswordAction) {
  const firebaseService: IFirebaseService = yield firebaseServicePromise

  try {
    yield put({ type: ActionType.SET_APP_BLOCKED })

    const isLoggedIn: IUser = yield select(Selectors.isLoggedIn)
    if (isLoggedIn) {
      yield put({ type: ActionType.LOG_OUT_REQUEST })

      // if something goes wrong with the logout we log it
      // but the user still can perform a login
      yield race({
        logoutSuccess: take(ActionType.LOGOUT_SUCCESS),
        logoutError: take(ActionType.LOGOUT_FAIL)
      })
    }

    yield call(firebaseService.loginWithEmailAndPassword, email, password)

    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(updateOnboardingActiveFlagSaga)
    yield call(logEvent, 'web_signInWithEmail_success')

    yield call(onSuccess)
    // `email` identifies whose sign-in this was: the flows that hand off to this
    // saga (USC, Wellhub) wait for their own outcome and must ignore everyone
    // else's — see waitForOwnAuthOutcome.
    yield put({ type: ActionType.SIGN_IN_WITH_EMAIL_AND_PASSWORD_SUCCESS, email })
  } catch (e) {
    yield call(logEvent, 'web_signInWithEmail_fail', e)

    yield call(onError, e)
    yield put({ type: ActionType.SIGN_IN_WITH_EMAIL_AND_PASSWORD_FAILED, email, error: e })
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* signInWithGoogleOneTapSaga({ idToken, onSuccess, onError }: ISignInWithGoogleOneTapAction) {
  yield put({ type: ActionType.SET_APP_BLOCKED })

  const firebaseService: IFirebaseService = yield firebaseServicePromise

  try {
    // logout the current user if it is not anonymous
    const currentUser: IUser = yield select(Selectors.getUser)

    if (currentUser?.providerData && !currentUser.providerData.isAnonymous) {
      yield put({ type: ActionType.LOG_OUT_REQUEST })

      // if something goes wrong with the logout we log it
      // but the user still can perform a login
      yield race({
        logoutSuccess: take(ActionType.LOGOUT_SUCCESS),
        logoutError: take(ActionType.LOGOUT_FAIL)
      })
    }

    yield call(firebaseService.signInWithGoogleOneTap, idToken)

    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(updateOnboardingActiveFlagSaga)

    yield call(logEvent, 'web_signInWithGoogleOneTap')

    yield call(onSuccess)
  } catch (e) {
    yield call(logEvent, 'web_signInWithGoogleOneTap_fail')

    yield call(onError, e)
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* signInWithGoogleSaga({ onSuccess, onError, onCancel }: ISignInWithGoogleRequestAction) {
  yield put({ type: ActionType.SET_APP_BLOCKED })

  const firebaseService: IFirebaseService = yield firebaseServicePromise

  const provider = new GoogleAuthProvider()
  provider.addScope('https://www.googleapis.com/auth/userinfo.email')

  try {
    // logout the current user if it is not anonymous
    const currentUser: IUser = yield select(Selectors.getUser)
    if (currentUser?.providerData && !currentUser.providerData.isAnonymous) {
      yield call(logEvent, 'web_userAlreadyLoggedIn')

      yield put({ type: ActionType.LOG_OUT_REQUEST })

      // if something goes wrong with the logout we log it
      // but the user still can perform a login
      yield race({
        logoutSuccess: take(ActionType.LOGOUT_SUCCESS),
        logoutError: take(ActionType.LOGOUT_FAIL)
      })
    }

    yield call(logEvent, 'web_signInWithGoogle_request')

    yield call(firebaseService.signInWithProvider, provider)

    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(updateOnboardingActiveFlagSaga)

    yield call(logEvent, 'web_signInWithGoogle_success')

    yield call(onSuccess)
  } catch (e) {
    if (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request') {
      yield call(logEvent, 'web_signInWithGoogle_closedByUser', e)
      if (onCancel) {
        yield call(onCancel)
      }
      return
    }

    yield call(logEvent, 'web_signInWithGoogle_fail')
    yield call(onError, e)
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* registerWithGoogleSaga({ onSuccess, onError }: IRegisterWithGoogleRequestAction) {
  yield put({ type: ActionType.SET_APP_BLOCKED })

  const firebaseService: IFirebaseService = yield firebaseServicePromise

  const provider = new GoogleAuthProvider()
  provider.addScope('https://www.googleapis.com/auth/userinfo.email')

  try {
    // logout the current user if it is not anonymous
    const currentUser: IUser = yield select(Selectors.getUser)
    if (currentUser?.providerData && !currentUser.providerData.isAnonymous) {
      yield call(logEvent, 'web_userAlreadyLoggedIn')

      yield put({ type: ActionType.LOG_OUT_REQUEST })

      // if something goes wrong with the logout we log it
      // but the user still can perform a login
      yield race({
        logoutSuccess: take(ActionType.LOGOUT_SUCCESS),
        logoutError: take(ActionType.LOGOUT_FAIL)
      })
    }

    yield call(logEvent, 'web_registerWithGoogle_request')

    yield call(firebaseService.createUserWithProvider, provider)

    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(updateOnboardingActiveFlagSaga)

    yield call(logEvent, 'web_registerWithGoogle_success')

    yield call(onSuccess)
  } catch (e) {
    if (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request') {
      yield call(logEvent, 'web_signInWithGoogle_closedByUser', e)
      return
    }

    yield call(logEvent, 'web_registerWithGoogle_fail')
    yield call(onError, e)
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* upgradeUserWithGoogleSaga({ onSuccess, onError }: IUpgradeUserWithGoogleRequestAction) {
  yield put({ type: ActionType.SET_APP_BLOCKED })

  const firebaseService: IFirebaseService = yield firebaseServicePromise

  const provider = new GoogleAuthProvider()
  provider.addScope('https://www.googleapis.com/auth/userinfo.email')

  try {
    // logout the current user if it is not anonymous
    const currentUser: IUser = yield select(Selectors.getUser)
    if (currentUser?.providerData && !currentUser.providerData.isAnonymous) {
      yield call(logEvent, 'web_userAlreadyLoggedIn')

      yield put({ type: ActionType.LOG_OUT_REQUEST })

      // if something goes wrong with the logout we log it
      // but the user still can perform a login
      yield race({
        logoutSuccess: take(ActionType.LOGOUT_SUCCESS),
        logoutError: take(ActionType.LOGOUT_FAIL)
      })
    }

    yield call(logEvent, 'web_upgradeUserWithGoogle_request')

    yield call(firebaseService.linkUserWithProvider, provider)

    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(updateOnboardingActiveFlagSaga)

    yield call(logEvent, 'web_upgradeUserWithGoogle_success')

    yield call(onSuccess)
  } catch (e) {
    if (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request') {
      yield call(logEvent, 'web_signInWithGoogle_closedByUser', e)
      return
    }

    yield call(logEvent, 'web_upgradeUserWithGoogle_fail')
    yield call(onError, e)
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* signInWithAppleSaga({ onSuccess, onError, onCancel }: ISignInWithAppleRequestAction) {
  yield put({ type: ActionType.SET_APP_BLOCKED })

  const firebaseService: IFirebaseService = yield firebaseServicePromise

  const provider = new OAuthProvider('apple.com')
  provider.addScope('email')
  provider.addScope('name')

  try {
    // logout the current user if it is not anonymous
    const currentUser: IUser = yield select(Selectors.getUser)
    if (currentUser && !currentUser.providerData.isAnonymous) {
      yield call(logEvent, 'web_userAlreadyLoggedIn')

      yield put({ type: ActionType.LOG_OUT_REQUEST })

      // if something goes wrong with the logout we log it
      // but the user still can perform a login
      yield race({
        logoutSuccess: take(ActionType.LOGOUT_SUCCESS),
        logoutError: take(ActionType.LOGOUT_FAIL)
      })
    }

    yield call(logEvent, 'web_signInWithApple_request')

    yield call(firebaseService.signInWithProvider, provider)

    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(updateOnboardingActiveFlagSaga)

    yield call(logEvent, 'web_signInWithApple_success')

    yield call(onSuccess)
  } catch (e) {
    if (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request') {
      yield call(logEvent, 'web_signInWithApple_closedByUser', e)
      if (onCancel) {
        yield call(onCancel)
      }
      return
    }

    yield call(logEvent, 'web_signInWithApple_fail')
    yield call(onError, e)
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* registerWithAppleSaga({ onSuccess, onError }: ISignInWithGoogleOneTapAction) {
  yield put({ type: ActionType.SET_APP_BLOCKED })

  const firebaseService: IFirebaseService = yield firebaseServicePromise

  const provider = new OAuthProvider('apple.com')
  provider.addScope('email')
  provider.addScope('name')

  try {
    // logout the current user if it is not anonymous
    const currentUser: IUser = yield select(Selectors.getUser)
    if (currentUser && !currentUser.providerData.isAnonymous) {
      yield call(logEvent, 'web_userAlreadyLoggedIn')

      yield put({ type: ActionType.LOG_OUT_REQUEST })

      // if something goes wrong with the logout we log it
      // but the user still can perform a login
      yield race({
        logoutSuccess: take(ActionType.LOGOUT_SUCCESS),
        logoutError: take(ActionType.LOGOUT_FAIL)
      })
    }

    yield call(logEvent, 'web_signInWithApple_request')

    yield call(firebaseService.createUserWithProvider, provider)

    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(updateOnboardingActiveFlagSaga)

    yield call(logEvent, 'web_registerWithApple_success')

    yield call(onSuccess)
  } catch (e) {
    if (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request') {
      yield call(logEvent, 'web_signInWithApple_closedByUser', e)
      return
    }

    yield call(logEvent, 'web_registerWithApple_fail', e)
    yield call(onError, e)
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* upgradeUserWithAppleSaga({ onSuccess, onError }: ISignInWithGoogleOneTapAction) {
  yield put({ type: ActionType.SET_APP_BLOCKED })

  const firebaseService: IFirebaseService = yield firebaseServicePromise

  const provider = new OAuthProvider('apple.com')
  provider.addScope('email')
  provider.addScope('name')

  try {
    // logout the current user if it is not anonymous
    const currentUser: IUser = yield select(Selectors.getUser)
    if (currentUser && !currentUser.providerData.isAnonymous) {
      yield call(logEvent, 'web_userAlreadyLoggedIn')

      yield put({ type: ActionType.LOG_OUT_REQUEST })

      // if something goes wrong with the logout we log it
      // but the user still can perform a login
      yield race({
        logoutSuccess: take(ActionType.LOGOUT_SUCCESS),
        logoutError: take(ActionType.LOGOUT_FAIL)
      })
    }

    yield call(logEvent, 'web_upgradeWithApple_request')

    yield call(firebaseService.linkUserWithProvider, provider)

    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(updateOnboardingActiveFlagSaga)

    yield call(logEvent, 'web_upgradeWithApple_success')

    yield call(onSuccess)
  } catch (e) {
    if (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request') {
      yield call(logEvent, 'web_signInWithApple_closedByUser', e)
      return
    }

    yield call(logEvent, 'web_upgradeWithApple_fail', e)
    yield call(onError, e)
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* updateUserRequestSaga({ user }: IUpdateUserRequestAction) {
  const oldUser: IUser = yield select(Selectors.getUser)
  const firebaseService: IFirebaseService = yield firebaseServicePromise

  yield call(firebaseService.updateUser, { ...oldUser, ...user })
}

function* updateUserChangesetSaga({ changeset }: IUpdateUserChangesetRequestAction) {
  const firebaseService: IFirebaseService = yield firebaseServicePromise

  yield call(firebaseService.updateUser, changeset)
}

function* saveOnboardingDataRequestSaga({ onboardingData, newsletterAccepted, email, resolve, reject }: ISaveOnboardingDataRequestAction) {
  try {
    const oldUser: IUser = yield select(Selectors.getUser)

    const convertedOnboardingData: Selections = convertToFirebaseFormat(onboardingData)
    const cleanedData: Selections = removeUndefined(convertedOnboardingData)

    const oldData = {
      newsletter: oldUser.newsletter,
      newsletterEmail: oldUser?.newsletterEmail,
      personal: oldUser.personal,
      stats: oldUser.stats
    }

    const changeset = {
      newsletter: newsletterAccepted,
      newsletterEmail: email,
      ...cleanedData
    }

    // If the data is the same there is no need to perform requests
    if (isEqual(oldData, changeset)) {
      yield call(resolve)
      return
    }

    yield put({ type: ActionType.SET_APP_BLOCKED })

    yield put({
      type: ActionType.UPDATE_USER_CHANGESET_REQUEST,
      changeset: changeset
    })

    // Wait until user callback from firebase and we have the user in our store
    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(logEvent, 'web_onboardingDataSaveSuccess')
  } catch (e) {
    yield call(logEvent, 'web_onboardingDataSaveFail', e)
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
    yield call(reject, e)
    return
  }

  yield put({ type: ActionType.SET_APP_UNBLOCKED })
  yield call(resolve)
}

/**
 * Called when user has correctly paid for a subscription
 * Resolves the purchase event and clears the URL so that the event is not duplicated
 * and navigates to the correct route
 */
function* resolvePurchaseSaga() {
  // First wait until the dust of initialization has settled down
  yield call(waitForStartup)
  yield call(waitForLogin)

  const searchParams = getSearchParams()

  const stripeSessionId = searchParams.get('session_id')
  const subscriptionId = searchParams.get('subscriptionId')
  const source = searchParams.get('source')
  const sanitizedSearchParams = getSanitizedMarketingSearchParams()
  const twclid = sanitizedSearchParams.get('twclid')
  const ttclid = sanitizedSearchParams.get('ttclid')

  let subscription: IStripeSubscription | IPaypalSubscription
  let type: 'stripe' | 'paypal'

  const paymentPath = getPaymentUrl()

  yield delay(1000)

  if (source === 'elements') {
    try {
      if (!subscriptionId) {
        throw new Error('Sub id not available for stripe elements purchase')
      }
      const subscriptionRes: { subscription: IStripeSubscription } = yield call(ApiService.fetchStripeSubscriptionViaId, subscriptionId)
      subscription = subscriptionRes.subscription
      type = 'stripe'
    } catch (e) {
      console.error('cannot resolve stripe elements purchase saga, user has no subscription!')
      const subscriptionError = new Error('Trying to resolve elements purchase without subscription')
      Sentry.captureException(subscriptionError)
      yield call(logEvent, 'web_purchase_noStripeSubscription')
      yield call(logEvent, 'web_purchase_syncFailed')

      // Current-visit params (secrets stripped, no resurrection) — same as sanitizeUrl.
      const searchParamsString = getCurrentVisitUrlParamsString()
      yield put(push(`${paymentPath}${searchParamsString}`))
      return
    }
  } else if (stripeSessionId) {
    try {
      const subscriptionRes: { subscription: IStripeSubscription } = yield call(ApiService.fetchSubscriptionViaSession, stripeSessionId)
      subscription = subscriptionRes.subscription
      type = 'stripe'
    } catch (e) {
      console.error('cannot resolve stripe purchase saga, user has no subscription!')
      const subscriptionError = new Error('Trying to resolve purchase without subscription')
      Sentry.captureException(subscriptionError)
      yield call(logEvent, 'web_purchase_noStripeSubscription')
      yield call(logEvent, 'web_purchase_syncFailed')
      yield put(push(paymentPath))
      return
    }
  } else {
    try {
      if (!subscriptionId) {
        throw new Error('Sub id not available for paypal purchase')
      }

      const subscriptionRes: { subscription: IStripeSubscription | IPaypalSubscription, type: 'paypal' } = yield call(ApiService.fetchPaypalSubscriptionViaId, subscriptionId)
      subscription = subscriptionRes.subscription
      type = 'paypal'
    } catch (e) {
      console.error('cannot resolve paypal purchase saga, user has no subscription!')
      const subscriptionError = new Error('Trying to resolve purchase without subscription')
      Sentry.captureException(subscriptionError)
      yield call(logEvent, 'web_purchase_noPaypalSubscription')
      yield call(logEvent, 'web_purchase_syncFailed')
      yield put(push(paymentPath))
      return
    }
  }

  let saleInfo
  if (type === 'stripe') {
    const stripeSubscription = subscription as IStripeSubscription
    const discountPercentage = Number(stripeSubscription.metadata.discountPercentage)
    const originalTrialPriceStr = stripeSubscription.metadata.originalTrialPrice

    let saleValue
    if (originalTrialPriceStr) {
      saleValue = Number(originalTrialPriceStr) * (discountPercentage / 100) / 100
    } else {
      saleValue = discountPercentage ? stripeSubscription.plan.amount * (discountPercentage / 100) / 100 : stripeSubscription.plan.amount / 100
    }

    const currency = stripeSubscription.plan.currency.toUpperCase()

    // Dedup id between the browser Meta Pixel + Signals Gateway and the
    // server-side Meta CAPI event. Generated at checkout start
    // (Stripe.service.ts) and stashed in sessionStorage; the same UUID was
    // persisted on subscription.metadata so the Stripe webhook fires the
    // server-side Purchase with the same id. Fall back to subscription.id
    // when sessionStorage was cleared / the user is in private mode — dedup
    // degrades but the conversion still imports.
    //
    // Kept on its own field because purchaseId is downstream propagated as
    // transaction_id / conversion_id / order_id to GA, Twitter, Taboola,
    // Snap, … and must remain the Stripe-side id for cross-platform
    // reconciliation against Stripe exports.
    const metaEventId = getMetaPurchaseEventId() ?? stripeSubscription.id

    saleInfo = {
      purchaseId: stripeSubscription.id,
      metaEventId,
      value: saleValue,
      currency,
      pricingId: stripeSubscription.plan.id,
      customerId: stripeSubscription.customer,
      affiliation: stripeSubscription.affiliation ?? 'stripe',
      twclid: twclid,
      ttclid: ttclid
    }
  } else {
    const paypalSubscription = subscription as IPaypalSubscription

    const saleValue: number = parseFloat(paypalSubscription.billing_info.last_payment.amount.value)
    const currency = paypalSubscription.billing_info.last_payment.amount.currency_code

    saleInfo = {
      purchaseId: paypalSubscription.id,
      metaEventId: getMetaPurchaseEventId() ?? paypalSubscription.id,
      value: saleValue,
      currency,
      pricingId: paypalSubscription.plan_id,
      customerId: paypalSubscription.subscriber.payer_id,
      affiliation: 'paypal',
      twclid: twclid,
      ttclid: ttclid
    }
  }

  const stripeSubscription = subscription as IStripeSubscription
  yield put({ type: ActionType.SET_SUBSCRIPTION_DETAILS, subscriptionDetails: stripeSubscription })

  if (searchParams.get('send_event') === 'true') {
    // Strip from the LIVE URL only (removeLiveUrlParams avoids resurrecting click-IDs).
    // session_id is already read above and stays in the persisted store for reloads, so
    // dropping it here keeps the purchase id out of analytics page_location.
    yield put(replace({ search: removeLiveUrlParams(['send_event', 'session_id']) }))

    yield call(logEvent, 'web_purchaseSuccess', saleInfo)
    yield call(saveSourceMetadataSaga, stripeSubscription.id, 'subscription')

    // Re-read trackedSubscriptions to minimize race condition and check for duplicates
    const trackedSubscriptions: string[] = JSON.parse(localStorage.getItem('trackedSubscriptions') ?? '[]')
    if (!trackedSubscriptions.includes(subscription.id)) {
      localStorage.setItem('trackedSubscriptions', JSON.stringify([...trackedSubscriptions, subscription.id]))
    }
  }

  yield put({ type: ActionType.COMMIT_PAYMENT_STEP, stepId: 'payment' })
}

function* signInWithFutokenSaga({ futoken, onSuccess, onError }: ISignInWithFutokenAction) {
  yield call(logEvent, 'web_token_sign_in_request')

  const firebaseService: IFirebaseService = yield firebaseServicePromise
  const isLoggedIn: ReturnType<typeof Selectors.isLoggedIn> = yield select(Selectors.isLoggedIn)

  // log out current user to avoid funny errors and broken states
  if (isLoggedIn) {
    yield put({ type: ActionType.LOG_OUT_REQUEST })

    // if something goes wrong with the logout we log it
    // but the user still can perform a login
    yield race({
      logoutSuccess: take(ActionType.LOGOUT_SUCCESS),
      logoutError: take(ActionType.LOGOUT_FAIL)
    })
  }

  const { error: jwtError, token } = yield call(ApiService.fetchJWT, futoken)

  if (jwtError) {
    yield call(logEvent, 'web_token_sign_in_fail', jwtError)
    yield call(onError, jwtError)
    return
  }

  try {
    yield call(firebaseService.signInWithToken, token)
  } catch (error) {
    yield call(logEvent, 'web_token_sign_in_fail', error)
    yield call(onError, error)
    return
  }

  // Wait until user callback from firebase and we have the user in our store
  yield take(ActionType.UPDATE_USER_SUCCESS)

  yield call(onSuccess)

  yield call(logEvent, 'web_token_sign_in_success')
}

function* updateOnboardingActiveFlagSaga() {
  const user: IUser = yield select(Selectors.getUser)

  const hasPersonalData = user.personal !== undefined
  const isOnboardingActive = user.onboardingActive
  const isWebUser = user.isWebUser

  // we want to guarantee that the user has the onboardingActive flag set to true
  // so we can show the onboarding screen on the app in case the user has not
  // completed it on the web or the user just signed up without performing the onboarding
  if (!hasPersonalData && !isOnboardingActive && isWebUser) {
    yield put({ type: ActionType.UPDATE_USER_CHANGESET_REQUEST, changeset: { onboardingActive: true } })

    yield take(ActionType.UPDATE_USER_SUCCESS)
  }
}

function* logoutRequestSaga({ nextRouteName }: ILogoutRequestAction) {
  const isUserLoggedIn: boolean = yield select(Selectors.isLoggedIn)

  if (isUserLoggedIn) {
    try {
      const firebaseService: IFirebaseService = yield firebaseServicePromise
      yield call(logEvent, 'web_logout')
      yield call(firebaseService.logout)
    } catch (error) {
      Sentry.captureException(error)
      yield call(logEvent, 'web_logoutFail', error)
      yield put({ type: ActionType.LOGOUT_FAIL })
    }
  } else {
    yield call(logEvent, 'web_logoutFail', { message: 'user not logged in' })
    yield put({ type: ActionType.LOGOUT_FAIL })
  }

  if (nextRouteName) {
    yield put(push(nextRouteName))
  }
}

function* checkEmailExistsSaga({ email, resolve }: ICheckEmailExistsRequestAction) {
  try {
    yield put({ type: ActionType.SET_APP_BLOCKED })
    const exists = (yield call(ApiService.checkEmailExists, email)) as boolean
    yield call(resolve, exists)
  } catch (e) {
    Sentry.captureException(e)
    yield call(resolve, false)
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* resetPasswordSaga({ email, onError, onSuccess }: IResetPasswordRequestAction) {
  try {
    const firebaseService: IFirebaseService = yield firebaseServicePromise
    yield call(firebaseService.resetPassword, email)

    yield call(onSuccess)
  } catch (e) {
    Sentry.captureException(e)
    if (e.code === 'auth/user-not-found') {
      yield call(onError, t('FirebaseError.auth/user-not-found'))
    } else {
      yield call(onError, t('resetPasswordModal.errorMessage'))
    }
  }
}

function* createStripeSessionSaga({ packages, selectedPackage, onSuccess, onError }: ICreateStripeSessionAction) {
  yield call(logEvent, 'web_startCheckout', { channel: 'stripe', ...selectedPackage })

  try {
    yield put({ type: ActionType.SET_APP_BLOCKED })
    const lang: string = yield call(getUserLanguage)
    const { recurringPackageId, trialId } = yield call(getRecurringAndTrialId, selectedPackage, selectedPackage.id, packages)
    const gender: IGender | null | undefined = yield select(Selectors.getGender)

    const successEndpoint: string = yield call(getPaymentSuccessUrl)
    const cancelEndpoint: string = yield call(getPaymentUrl)

    const { sessionId, url }: { sessionId: string, url?: string } = yield call(createStripeCheckoutSession, recurringPackageId, lang, trialId, !!selectedPackage.discountedPrice, gender, successEndpoint, cancelEndpoint)

    yield call(logEvent, 'web_stripeSessionCreationSuccess')
    onSuccess(sessionId, url)
  } catch (e) {
    yield call(logEvent, 'web_stripeSessionCreationException', { e })
    onError(e)
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

function* handleNavigatingToPostOnboardingRouteSaga() {
  const currentUser: (IUser | undefined) = yield select(Selectors.getUser)

  if (!currentUser) {
    yield call(logEvent, 'web_navigateToPostOnboardingRoutesFail', { message: 'no existing user' })
    yield put(push(RouteNames.LOADING))
    return
  }

  const isRegisteredUser: boolean = yield select(Selectors.isRegisteredUser)

  let hasSubscription = false
  hasSubscription = yield call(isUserSubscribed, currentUser)

  const purchaseAvailable: boolean = yield call(isPurchaseAvailable)

  if (!hasSubscription && !purchaseAvailable) {
    // Track how many users are skipping the upsell screen. See: https://bodyfastworkspace.slack.com/archives/CDAJ9JY6T/p1719394042749919?thread_ts=1719391425.202899&cid=CDAJ9JY6T
    yield call(logEvent, 'web_upsellSkipped')
  }

  const flow = (yield call(getCurrentFlow)) as string

  // The voucher flow owns its own navigation (redeem first, then the download
  // screen) and has no payment or upgrade route to fall back on, so the generic
  // post-onboarding routing must not fire underneath it.
  if (flow === Flows.USC) {
    yield call(logEvent, 'web_navigateToPostOnboardingSkipped', { flow })
    return
  }

  if (flow === 'wellhub') {
    yield put(push(RouteNames.WELLHUB_DOWNLOAD, { hasOldSubscription: hasSubscription }))
  } else if (!hasSubscription && purchaseAvailable) {
    const paymentRoute: string = yield call(getPaymentUrl)
    yield put(push(paymentRoute))
  } else if (!isRegisteredUser) {
    const registrationRoute: string = yield call(getUpgradeAccountUrl)
    yield put(push(registrationRoute))
  } else {
    const downloadRoute: string = yield call(getDownloadUrl)
    yield put(push(downloadRoute, { hasOldSubscription: hasSubscription }))
  }
}

function* registerWithEmailAndPasswordSaga({ email, password, name, reject, resolve }: IRegisterWithEmailAndPassword) {
  try {
    yield call(logEvent, 'web_registerWithEmailAndPass_request')

    yield put({ type: ActionType.SET_APP_BLOCKED })

    yield put({ type: ActionType.LOG_OUT_REQUEST })
    yield race({
      logoutSuccess: take(ActionType.LOGOUT_SUCCESS),
      logoutError: take(ActionType.LOGOUT_FAIL)
    })

    yield put({
      type: ActionType.SIGN_UP_WITH_EMAIL_REQUEST,
      email,
      password,
      onSuccess: () => { },
      // Failures are handled by the race below so that this saga always finishes.
      onError: () => { }
    })

    const signUpResult = (yield race({
      signUpSuccess: take(ActionType.UPDATE_USER_SUCCESS),
      signUpFailed: take(ActionType.SIGN_UP_WITH_EMAIL_FAILED)
    })) as { signUpSuccess?: unknown, signUpFailed?: { error: FirebaseError } }

    if (signUpResult.signUpFailed) {
      throw signUpResult.signUpFailed.error
    }

    yield put({ type: ActionType.UPDATE_USER_CHANGESET_REQUEST, changeset: { name } })
    yield take(ActionType.UPDATE_USER_SUCCESS)

    yield call(resolve)
    yield call(logEvent, 'web_registerWithEmailAndPass_success')
    // See the note on SIGN_IN_WITH_EMAIL_AND_PASSWORD_SUCCESS above.
    yield put({ type: ActionType.REGISTER_WITH_EMAIL_AND_PASSWORD_SUCCESS, email })
    yield put({ type: ActionType.HANDLE_NAVIGATING_TO_POST_ONBOARDING_ROUTE })
  } catch (e) {
    yield call(logEvent, 'web_registerWithEmailAndPass_fail', e)
    // The error travels on the action too — flows that wait on it (USC, Wellhub)
    // report it themselves instead of going through `reject`.
    yield put({ type: ActionType.REGISTER_WITH_EMAIL_AND_PASSWORD_FAILED, email, error: e })
    yield call(reject, e)
  } finally {
    yield put({ type: ActionType.SET_APP_UNBLOCKED })
  }
}

const APP_STATE_PARAMS_TO_REMOVE = [
  'appState',
  'tokenId',
  'token',
  'dc',
  'previousStep',
  'lastSavedStep',
  'lastSavedHistoryLength',
  'payment_intent',
  'payment_intent_client_secret',
  'selectedPackageId',
  'subscriptionId',
  'send_event',
  'redirect_status',
  'source',
  // Checkout session ids resolve exactly one purchase; a stale one would resolve the
  // previous purchase on the next funnel's success page.
  'session_id',
  'ebook_session_id'
]

function* clearAppDataSaga() {
  yield call(logEvent, 'web_clearAppDataStart')
  // do not call localStorage.clear() as it affects adminarea and other endpoints under same host

  yield call(removeSearchParams, APP_STATE_PARAMS_TO_REMOVE)

  yield call(logEvent, 'web_clearAppDataFinished')

  // logout any previous user in case of multiple consecutive purchases, so by the time the email is provided a new anonymous account is created
  yield put({ type: ActionType.LOG_OUT_REQUEST })

  const countdownExpiration = localStorage.getItem('dscExp')

  if (!countdownExpiration || !DefaultConfig.WITH_DISCOUNT_COOLDOWN_RESET) {
    return
  }

  const countdownExpirationTimestamp = parseInt(countdownExpiration)
  const currentTime = yield* call(Date.now)
  const shouldResetCooldown = currentTime - countdownExpirationTimestamp > DefaultConfig.DISCOUNT_COOLDOWN_RESET_TIMEOUT

  if (shouldResetCooldown) {
    localStorage.removeItem('dscExp')
  }
}

function* loadingCompletedSaga() {
  yield put({ type: ActionType.LOADING_FINISHED })

  const user: ReturnType<typeof Selectors.getUser> = yield select(Selectors.getUser)

  if (user?.providerData?.isAnonymous && !!user?.subscription) {
    yield call(logEvent, 'web_loadingScreenToUpgradeAccount')
    yield put(push(RouteNames.UPGRADE_ACCOUNT))
  } else {
    yield call(logEvent, 'web_loadingScreenToOnboarding')
    const firstStep = yield* call(getFirstStep)
    yield put(push(ONBOARDING_ROUTES_BY_STEP_ID[firstStep.id]))
  }
}

// Debug-panel trigger: verifies the saga crash machinery end-to-end
// (web_sagaCrashError report + containment, app keeps reacting).
function* simulateSagaCrashSaga() {
  yield delay(0)
  throw new Error('Simulated saga crash (debug panel)')
}

export function* AppSagas() {
  yield all([
    takeLatestSafe(ActionType.SAVE_ONBOARDING_DATA_REQUEST, saveOnboardingDataRequestSaga),
    takeLatestSafe(ActionType.LOG_OUT_REQUEST, logoutRequestSaga),
    takeLatestSafe(ActionType.UPDATE_USER_SUCCESS, updateUserSuccessSaga),
    takeLatestSafe(ActionType.UPDATE_USER_REQUEST, updateUserRequestSaga),
    takeLatestSafe(ActionType.UPDATE_USER_CHANGESET_REQUEST, updateUserChangesetSaga),
    takeLatestSafe(ActionType.ANONYMOUS_SIGN_IN_REQUEST, anonymousSignInSaga),
    takeLatestSafe(ActionType.SIGN_IN_WITH_FUTOKEN_REQUEST, signInWithFutokenSaga),
    takeLatestSafe(ActionType.RESOLVE_PURCHASE, resolvePurchaseSaga),
    takeLatestSafe(ActionType.SIGN_UP_WITH_EMAIL_REQUEST, signUpWithEmailSaga),
    takeLatestSafe(ActionType.SIGN_IN_WITH_GOOGLE_ONE_TAP, signInWithGoogleOneTapSaga),
    takeLatestSafe(ActionType.SIGN_IN_WITH_EMAIL_AND_PASSWORD_REQUEST, signInWithEmailAndPasswordSaga),
    takeLatestSafe(ActionType.SIGN_IN_WITH_GOOGLE_REQUEST, signInWithGoogleSaga),
    takeLatestSafe(ActionType.SIGN_IN_WITH_APPLE_REQUEST, signInWithAppleSaga),
    takeLatestSafe(ActionType.REGISTER_WITH_GOOGLE_REQUEST, registerWithGoogleSaga),
    takeLatestSafe(ActionType.REGISTER_WITH_APPLE_REQUEST, registerWithAppleSaga),
    takeLatestSafe(ActionType.UPGRADE_USER_WITH_GOOGLE_REQUEST, upgradeUserWithGoogleSaga),
    takeLatestSafe(ActionType.UPGRADE_USER_WITH_APPLE_REQUEST, upgradeUserWithAppleSaga),
    takeLatestSafe(ActionType.CHECK_EMAIL_EXISTS_REQUEST, checkEmailExistsSaga),
    takeLatestSafe(ActionType.RESET_PASSWORD_REQUEST, resetPasswordSaga),
    takeLatestSafe(ActionType.CREATE_STRIPE_SESSION_REQUEST, createStripeSessionSaga),
    takeLatestSafe(ActionType.HANDLE_NAVIGATING_TO_POST_ONBOARDING_ROUTE, handleNavigatingToPostOnboardingRouteSaga),
    takeLatestSafe(ActionType.REGISTER_WITH_EMAIL_AND_PASSWORD, registerWithEmailAndPasswordSaga),
    takeLatestSafe(ActionType.CLEAR_APP_DATA, clearAppDataSaga),
    takeLatestSafe(ActionType.LOADING_COMPLETED, loadingCompletedSaga),
    // Registered only off-production; the debug panel is the sole dispatcher.
    ...(__DEV__ ? [takeLatestSafe(ActionType.SIMULATE_SAGA_CRASH, simulateSagaCrashSaga)] : [])
  ])
}
